import { Flex } from "antd";
import { useMemo } from "react";
import { AvatarShape, Avatar, TabKeys, ITEM_TYPES } from "../../../../types";
import {
  getAvatarImagePath,
  mapRelativeToPixels,
  resolveColorToBrandkitColorIfNeeded,
  getBackgroundStyle,
} from "../../../../helpers/itemsHelper";
import { AnimationPreviewWrapper } from "./AnimationPreviewWrapper";
import { DraggableComponent } from "./DraggableComponent";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { useTemplateData } from "../../../../hooks/useTemplateData";

export const Presenter = ({
  presenter,
  previewHeight,
  previewWidth,
  draggable = false,
}: {
  presenter: Avatar;
  previewHeight: number;
  previewWidth: number;
  draggable?: boolean;
}) => {
  const avatarHasShape = presenter.avatarShape !== AvatarShape.Regular;

  const { setActiveTab, activeTab } = useTemplatesStatusStore((state) => ({
    setActiveTab: state.setActiveTab,
    activeTab: state.activeTab,
  }));

  const setPresenter = useTemplateEditorStore((store) => store.setPresenter);

  const {
    brandKitData: { brandKitPalette },
  } = useTemplateData();

  const avatarImagePath = useMemo(() => {
    return getAvatarImagePath(presenter);
  }, [presenter]);

  const avatarWidth = useMemo(() => {
    if (presenter.avatarShape === AvatarShape.Regular) {
      return mapRelativeToPixels(presenter.size.width, previewWidth);
    }
    return mapRelativeToPixels(presenter.size.height, previewHeight);
  }, [
    presenter.avatarShape,
    presenter.size.height,
    presenter.size.width,
    previewHeight,
    previewWidth,
  ]);

  const borderRadius = useMemo(() => {
    if (presenter.avatarShape === AvatarShape.Circle) {
      return "50%";
    }
    if (presenter.stroke?.radius) {
      return `${presenter.stroke.radius * previewWidth}px`;
    }
    return "unset";
  }, [presenter.avatarShape, presenter.stroke, previewWidth]);

  if (!avatarImagePath) return null;

  const resolvedBackground = resolveColorToBrandkitColorIfNeeded(
    presenter.background,
    brandKitPalette
  );
  const resolvedStrokeColor = resolveColorToBrandkitColorIfNeeded(
    presenter.stroke?.color ?? undefined,
    brandKitPalette
  );

  const innerComponent = (
    <Flex
      data-testid="presenter"
      align="center"
      justify="center"
      style={{
        borderRadius: borderRadius,
        ...(avatarHasShape && resolvedBackground
          ? getBackgroundStyle(resolvedBackground, brandKitPalette)
          : { backgroundColor: "transparent" }),
        overflow: "hidden",
        border:
          presenter.stroke?.width && resolvedStrokeColor
            ? `solid ${presenter.stroke.width * previewWidth}px ${resolvedStrokeColor}`
            : "none",
        width: avatarWidth,
        height: mapRelativeToPixels(presenter.size.height, previewHeight),
        zIndex: presenter.layerIndex,
        ...(!draggable && {
          position: "absolute",
          top: mapRelativeToPixels(presenter.position.y, previewHeight),
          left: mapRelativeToPixels(presenter.position.x, previewWidth),
          transformOrigin: "center",
          transform: presenter.rotation
            ? `rotate(${presenter.rotation}deg)`
            : undefined,
        }),
      }}
    >
      <img
        src={avatarImagePath}
        alt="Presenter"
        draggable="false"
        style={{
          width: "auto",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center top",
        }}
      />
    </Flex>
  );

  if (!draggable) {
    return innerComponent;
  }

  return (
    <DraggableComponent
      previewWidth={previewWidth}
      previewHeight={previewHeight}
      position={presenter.position}
      size={
        presenter.avatarShape === AvatarShape.Regular
          ? presenter.size
          : {
              ...presenter.size,
              width: (presenter.size.height * previewHeight) / previewWidth,
            }
      }
      layerIndex={presenter.layerIndex}
      rotation={presenter.rotation}
      saveRatio={true}
      resize={true}
      drag={true}
      item={{
        type: ITEM_TYPES.Presenter,
      }}
      onDragStop={setPresenter}
      onRotate={(rotation) => setPresenter({ rotation })}
      onFocus={() => {
        if (activeTab !== TabKeys.avatar && activeTab !== TabKeys.motion) {
          setActiveTab(TabKeys.avatar);
        }
      }}
    >
      <AnimationPreviewWrapper
        animations={presenter.animations}
        previewWidth={previewWidth}
        previewHeight={previewHeight}
        itemId={ITEM_TYPES.Presenter}
        rotation={presenter.rotation}
      >
        {innerComponent}
      </AnimationPreviewWrapper>
    </DraggableComponent>
  );
};
